class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.aurhaInitTimer();
  }

  /* Aurha: cronômetro de reserva persistido em sessionStorage.
     Re-executado a cada render (o markup do timer volta novo). */
  aurhaInitTimer() {
    const el = this.querySelector('[data-aurha-timer]');
    if (!el) return;
    const valueEl = el.querySelector('[data-aurha-timer-value]');
    if (!valueEl) return;

    const minutes = parseInt(el.getAttribute('data-minutes'), 10) || 10;
    const durationMs = minutes * 60 * 1000;
    const KEY = 'aurhaCartReserveUntil';

    let until = parseInt(sessionStorage.getItem(KEY), 10);
    if (!until || isNaN(until) || until < Date.now()) {
      until = Date.now() + durationMs;
      sessionStorage.setItem(KEY, String(until));
    }

    if (this._aurhaTimerInterval) clearInterval(this._aurhaTimerInterval);
    const tick = () => {
      const remaining = Math.max(0, until - Date.now());
      const totalSec = Math.floor(remaining / 1000);
      const m = String(Math.floor(totalSec / 60)).padStart(2, '0');
      const s = String(totalSec % 60).padStart(2, '0');
      const liveValue = this.querySelector('[data-aurha-timer-value]');
      if (liveValue) liveValue.textContent = `${m}:${s}`;
      if (remaining <= 0 && this._aurhaTimerInterval) clearInterval(this._aurhaTimerInterval);
    };
    tick();
    this._aurhaTimerInterval = setInterval(tick, 1000);
  }

  /* Aurha: dispara o confete se ligado no tema */
  aurhaConfettiIfEnabled() {
    if (this.getAttribute('data-aurha-confetti') === 'true' && typeof window.aurhaConfetti === 'function') {
      window.aurhaConfetti();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('aurha-cart-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('aurha-cart-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Aurha: adicionar item com o carrinho vazio (sugestões "Mais vendidos")
    // volta o markup preenchido, mas a classe is-empty fica presa no
    // <cart-drawer> — o CSS (component-cart-drawer.css) esconde o header e
    // comprime o inner, então os itens não aparecem até recarregar a página.
    // renderContents só roda após um /cart/add bem-sucedido, logo o carrinho
    // nunca está vazio aqui: limpamos is-empty do próprio elemento e dos filhos.
    this.classList.remove('is-empty');
    this.querySelectorAll('.is-empty').forEach((el) => el.classList.remove('is-empty'));
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.aurhaInitTimer();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* Aurha: botão "Adicionar" das sugestões do carrinho.
   Reusa o mesmo pipeline do tema (/cart/add.js + renderContents). */
class AurhaUpsellAdd extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.variantId = this.getAttribute('data-variant-id');
    // Aurha xsell: lugar do bloco (infantil-pdp | infantil-cart) pra medir o teste; vazio nas sugestões comuns.
    this.xsellPlace = this.getAttribute('data-aurha-xsell') || '';
    // Aurha xsell na PDP: adiciona sem abrir o drawer e marca "✓ No carrinho" no próprio botão
    // (decisão do Augusto em 15/10/2026, no lugar de abrir o carrinho).
    this.stay = this.hasAttribute('data-aurha-xsell-stay');
    this.addedLabel = this.getAttribute('data-aurha-xsell-added') || 'No carrinho';
    this.added = false;
    if (this.button) this.button.addEventListener('click', this.add.bind(this));
  }

  add() {
    const cart = document.querySelector('cart-drawer');
    if (!cart || !this.variantId || this.added) return;
    this.button.setAttribute('aria-busy', 'true');

    const sections = cart.getSectionsToRender().map((s) => s.id);
    const payload = {
      id: this.variantId,
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname,
    };
    // Propriedade oculta de linha: se a Yampi repassar, o pedido diz de onde a infantil veio.
    if (this.xsellPlace) payload.properties = { _aurha_xsell: this.xsellPlace };
    const body = JSON.stringify(payload);

    fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body,
    })
      .then((response) => response.json())
      .then((parsed) => {
        if (parsed.status) {
          this.button.removeAttribute('aria-busy');
          return;
        }
        if (this.stay) {
          this.renderQuiet(cart, parsed);
          this.markAdded();
        } else {
          cart.renderContents(parsed);
          this.hideCard();
        }
        this.track();
      })
      .catch(() => {
        this.button.removeAttribute('aria-busy');
      });
  }
  // Mesmo que cart.renderContents, sem abrir o drawer: atualiza o conteúdo e a bolha do ícone,
  // pra quando a cliente abrir o carrinho ele já estar certo.
  renderQuiet(cart, parsedState) {
    cart.classList.remove('is-empty');
    cart.querySelectorAll('.is-empty').forEach((el) => el.classList.remove('is-empty'));
    cart.productId = parsedState.id;
    cart.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;
      sectionElement.innerHTML = cart.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    const overlay = cart.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', cart.close.bind(cart));
  }

  // Botão vira "✓ No carrinho" e para de responder; o card fica na página.
  markAdded() {
    this.added = true;
    this.button.removeAttribute('aria-busy');
    this.button.classList.add('is-added');
    this.button.setAttribute('aria-disabled', 'true');
    this.button.innerHTML =
      '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '<span></span>';
    this.button.querySelector('span').textContent = this.addedLabel;
    this.button.setAttribute('aria-label', this.addedLabel);
  }

  // Na PDP o bloco não re-renderiza sozinho: tira o card que acabou de entrar no carrinho
  // (no drawer o Liquid já faz isso na re-renderização). Bloco sem card some.
  hideCard() {
    if (!this.xsellPlace) return;
    const card = this.closest('.aurha-xsell__card');
    if (!card) return;
    const block = card.closest('.aurha-xsell');
    card.remove();
    if (block && !block.querySelector('.aurha-xsell__card')) block.remove();
  }

  // Medição do "comprado junto": dataLayer (GTM) + Clarity (evento e tags). Nunca quebra o add.
  // data-aurha-xsell = "<lugar>:<origem>:<companheiro>" (lugar = pdp | cart). Valores antigos (infantil-pdp) ainda passam.
  track() {
    if (!this.xsellPlace) return;
    const t = AurhaXsell.parse(this.xsellPlace);
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'aurha_xsell_add', xsell_place: t.place, xsell_origin: t.origin, xsell_partner: t.partner, xsell_tag: this.xsellPlace, variant_id: this.variantId });
    } catch (e) {}
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('event', 'xsell_add_' + t.place);
        window.clarity('set', 'xsell', t.place);
        if (t.partner) window.clarity('set', 'xsell_par', t.origin + '>' + t.partner);
      }
    } catch (e) {}
  }
}

/* Visualização do bloco "comprado junto" (o denominador da medição por par). Um evento por bloco que entra
   em pelo menos metade na tela, inclusive o do drawer a cada re-renderização (é outro elemento). */
const AurhaXsell = {
  parse(tag) {
    const p = String(tag || '').split(':');
    return p.length >= 3 ? { place: p[0], origin: p[1], partner: p.slice(2).join(':') } : { place: p[0] || '', origin: '', partner: '' };
  },
  view(block) {
    const tags = Array.from(block.querySelectorAll('[data-aurha-xsell]')).map((c) => c.getAttribute('data-aurha-xsell'));
    const first = AurhaXsell.parse(tags[0] || block.getAttribute('data-aurha-xsell-block'));
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'aurha_xsell_view', xsell_place: first.place, xsell_origin: first.origin, xsell_partners: tags.map((t) => AurhaXsell.parse(t).partner), xsell_tags: tags });
    } catch (e) {}
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('event', 'xsell_view_' + first.place);
        tags.forEach((t) => { const x = AurhaXsell.parse(t); if (x.partner) window.clarity('set', 'xsell_view', x.origin + '>' + x.partner); });
      }
    } catch (e) {}
  },
  start() {
    if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { io.unobserve(e.target); AurhaXsell.view(e.target); } });
    }, { threshold: 0.5 });
    const watch = (root) => {
      if (!root.querySelectorAll) return;
      root.querySelectorAll('[data-aurha-xsell-block]:not([data-aurha-xsell-seen])').forEach((el) => { el.setAttribute('data-aurha-xsell-seen', '1'); io.observe(el); });
    };
    watch(document);
    new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1) watch(n); })))
      .observe(document.documentElement, { childList: true, subtree: true });
  },
};
try { AurhaXsell.start(); } catch (e) {}

customElements.define('upsell-add', AurhaUpsellAdd);

/* Aurha: clicar no - (menos) com quantidade 1 remove o item do carrinho
   (mesma ação da lixeira). Roda na fase de captura, antes do handler do tema. */
document.addEventListener(
  'click',
  function (event) {
    var minus = event.target.closest && event.target.closest('.cart-item button[name="minus"]');
    if (!minus) return;
    var item = minus.closest('.cart-item');
    if (!item) return;
    var input = item.querySelector('input.quantity__input');
    if (!input) return;
    if (parseInt(input.value, 10) <= 1) {
      event.preventDefault();
      event.stopImmediatePropagation();
      var removeEl = item.querySelector('cart-remove-button');
      if (removeEl) removeEl.click();
    }
  },
  true
);
